import { useState, useEffect } from 'react';
import { X, Phone, Mail, Globe, MapPin, Calendar, Sparkles, Check, RefreshCw, Zap, Clock, Copy, ChevronRight, Send, Loader2, Building } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { API, SECTEURS, RESULTATS_APPEL, PROCHAINES_ACTIONS, StatutBadge, SecteurTag, ScoreBadge, generateContextTips } from './constants';

export function GoProspecter({ prospects, onUpdate, onRefresh, catalogue, onClose }) {
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(0);
  const [sessionStats, setSessionStats] = useState({ appels: 0, emails: 0, passes: 0, relances: 0, total: 0 });
  const [action, setAction] = useState(null);
  const [callResult, setCallResult] = useState('');
  const [callNote, setCallNote] = useState('');
  const [relanceDate, setRelanceDate] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailVersions, setEmailVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(0);
  const [emailForm, setEmailForm] = useState({ subject: '', body: '', sender: 'restaurant' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [aiTips, setAiTips] = useState(null);

  useEffect(() => {
    const relances = prospects.filter(p => p.statut === 'relance' && !['gagne', 'perdu'].includes(p.statut));
    const nouveaux = prospects.filter(p => p.statut === 'nouveau');
    const contactes = prospects.filter(p => p.statut === 'contacte');
    const ordered = [...relances, ...nouveaux, ...contactes];
    setQueue(ordered);
  }, [prospects]);

  const p = queue[current];
  const progress = queue.length > 0 ? Math.round(((current) / queue.length) * 100) : 0;
  const sectInfo = SECTEURS[p?.secteur] || {};
  const SectIcon = sectInfo.icon || Building;

  useEffect(() => {
    if (!p) return;
    setAiTips(null);
    setShowHistory(false);
    const comms = p.communications || [];
    const lastComm = comms[comms.length - 1];
    const tips = generateContextTips(p, comms, lastComm);
    setAiTips(tips);
  }, [current, p?.id]);

  const nextProspect = (actionType) => {
    setSessionStats(prev => ({ ...prev, [actionType]: prev[actionType] + 1, total: prev.total + 1 }));
    setAction(null);
    setCallResult('');
    setCallNote('');
    setEmailVersions([]);
    setEmailForm({ subject: '', body: '', sender: 'restaurant' });
    if (current + 1 < queue.length) {
      setCurrent(current + 1);
    } else {
      setShowDone(true);
    }
  };

  const logCall = async () => {
    if (!callResult) return;
    const resultObj = RESULTATS_APPEL.find(r => r.value === callResult);
    const comm = { type: 'appel', date: new Date().toISOString().split('T')[0], resultat: callResult, notes: callNote };
    const updates = { communications: [...(p.communications || []), comm] };
    if (resultObj?.auto) updates.statut = resultObj.auto;
    try {
      await fetch(`${API}/api/crm/prospects/${p.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) });
      toast.success(`Appel enregistre — ${resultObj?.label}`);
      onRefresh();
      nextProspect('appels');
    } catch { toast.error('Erreur'); }
  };

  const generateEmail = async () => {
    setEmailLoading(true);
    try {
      const res = await fetch(`${API}/api/crm/prospects/${p.id}/generate-email-ia`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: p.statut === 'relance' ? 'relance' : p.statut === 'contacte' ? 'relance' : 'premier_contact',
          context: (p.communications || []).slice(-3).map(c => `${c.date} ${c.type}: ${c.resultat || ''} ${c.notes || ''}`).join(' | '),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        const versions = data.versions || [];
        setEmailVersions(versions);
        if (versions.length > 0) {
          setEmailForm({ subject: versions[0].subject, body: versions[0].body, sender: 'restaurant' });
          setSelectedVersion(0);
        }
      }
    } catch { toast.error('Erreur IA'); }
    setEmailLoading(false);
  };

  const sendEmail = async () => {
    setSendingEmail(true);
    try {
      const res = await fetch(`${API}/api/crm/prospects/${p.id}/send-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailForm),
      });
      if (res.ok) {
        toast.success('Email envoye !');
        onRefresh();
        nextProspect('emails');
      } else { toast.error('Erreur envoi'); }
    } catch { toast.error('Erreur envoi'); }
    setSendingEmail(false);
  };

  const scheduleRelance = async () => {
    if (!relanceDate) return;
    try {
      await fetch(`${API}/api/crm/prospects/${p.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ relanceDate, statut: 'relance' }),
      });
      toast.success('Relance planifiee');
      onRefresh();
      nextProspect('relances');
    } catch { toast.error('Erreur'); }
  };

  if (!p || showDone) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-[#1B2A52] to-[#0F1829] z-50 flex items-center justify-center" data-testid="go-prospecter-done">
        <div className="text-center text-white space-y-6 px-6">
          <div className="w-20 h-20 rounded-full bg-[#009893]/20 flex items-center justify-center mx-auto"><Check size={40} className="text-[#009893]" /></div>
          <h2 className="text-2xl font-bold">Session terminee !</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white/10 rounded-xl p-4"><p className="text-2xl font-bold text-green-400">{sessionStats.appels}</p><p className="text-xs text-white/50">Appels</p></div>
            <div className="bg-white/10 rounded-xl p-4"><p className="text-2xl font-bold text-blue-400">{sessionStats.emails}</p><p className="text-xs text-white/50">Emails</p></div>
            <div className="bg-white/10 rounded-xl p-4"><p className="text-2xl font-bold text-amber-400">{sessionStats.relances}</p><p className="text-xs text-white/50">Relances</p></div>
          </div>
          <p className="text-sm text-white/60">{sessionStats.total} prospects traites</p>
          <Button onClick={onClose} className="bg-[#009893] hover:bg-[#00807b] text-white px-8" data-testid="go-prospecter-finish">Terminer</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-[#1B2A52] to-[#0F1829] z-50 flex flex-col" data-testid="go-prospecter">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="text-white/50 hover:text-white p-1" data-testid="go-prospecter-close"><X size={20} /></button>
          <span className="text-sm font-bold text-white">GO PROSPECTER</span>
          <span className="text-xs text-white/30">{current + 1} / {queue.length}</span>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <span className="text-green-400 flex items-center gap-1"><Phone size={12} /> {sessionStats.appels}</span>
          <span className="text-blue-400 flex items-center gap-1"><Mail size={12} /> {sessionStats.emails}</span>
          <span className="text-amber-400 flex items-center gap-1"><Clock size={12} /> {sessionStats.relances}</span>
        </div>
      </div>
      <div className="h-1 bg-white/5"><div className="h-full bg-[#009893] transition-all duration-500" style={{ width: `${progress}%` }} /></div>

      <div className="flex-1 overflow-y-auto flex items-start justify-center p-4 sm:p-6">
        <div className="w-full max-w-lg space-y-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
            <div className="flex items-start justify-between mb-3">
              <div>
                <StatutBadge statut={p.statut} size="xs" />
                <h2 className="text-xl font-bold text-white mt-1">{p.nom}</h2>
                <p className="text-sm text-white/60">{p.contact}{p.poste ? ` — ${p.poste}` : ''}</p>
              </div>
              <div className="flex items-center gap-2">
                <SecteurTag secteur={p.secteur} />
                <ScoreBadge score={p.scoreIA} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {p.tel && <div className="flex items-center gap-2 text-white/70"><Phone size={13} className="text-green-400" /><span>{p.tel}</span></div>}
              {p.email && <div className="flex items-center gap-2 text-white/70 truncate"><Mail size={13} className="text-blue-400" /><span className="truncate">{p.email}</span></div>}
              {p.ville && <div className="flex items-center gap-2 text-white/70"><MapPin size={13} className="text-amber-400" /><span>{p.ville}</span></div>}
              {p.siteWeb && <div className="flex items-center gap-2 text-white/70 truncate"><Globe size={13} className="text-purple-400" /><span className="truncate">{p.siteWeb}</span></div>}
            </div>
            {p.note && <p className="text-xs text-white/40 mt-2 border-t border-white/5 pt-2">{p.note}</p>}
            {p.relanceDate && <p className="text-xs text-amber-400 mt-1 flex items-center gap-1"><Clock size={11} /> Relance prevue: {p.relanceDate}</p>}
            {(p.communications || []).length > 0 && (
              <button onClick={() => setShowHistory(!showHistory)} className="w-full mt-2 text-[10px] text-white/30 hover:text-white/50 border-t border-white/5 pt-2 flex items-center justify-center gap-1" data-testid="go-show-history">
                <Clock size={10} /> {(p.communications || []).length} echange{(p.communications || []).length > 1 ? 's' : ''} {showHistory ? '\u25B2' : '\u25BC'}
              </button>
            )}
            {showHistory && (
              <div className="mt-2 space-y-1.5 max-h-32 overflow-y-auto">
                {(p.communications || []).slice().reverse().map((c, i) => (
                  <div key={i} className="flex items-start gap-2 text-[10px] text-white/50 bg-white/5 rounded-lg px-2.5 py-1.5">
                    <span className="text-white/30 shrink-0">{c.date}</span>
                    <span className={`shrink-0 ${c.type === 'appel' ? 'text-green-400' : 'text-blue-400'}`}>{c.type === 'appel' ? 'TEL' : 'MAIL'}</span>
                    {c.resultat && <span className="text-amber-300 shrink-0">{c.resultat}</span>}
                    {c.notes && <span className="text-white/40 truncate">{c.notes}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {aiTips && !action && (
            <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 backdrop-blur-sm rounded-2xl p-4 border border-purple-500/20" data-testid="go-ai-tips">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={14} className="text-purple-400" />
                <span className="text-xs font-bold text-purple-300">{aiTips.contexte}</span>
              </div>
              {aiTips.ouverture && (
                <div className="bg-white/5 rounded-lg px-3 py-2 mb-2">
                  <p className="text-[10px] text-white/30 mb-0.5">PHRASE D'ACCROCHE</p>
                  <p className="text-xs text-white/80 italic leading-relaxed">{aiTips.ouverture}</p>
                  <button onClick={() => { navigator.clipboard.writeText(aiTips.ouverture.replace(/"/g, '')); toast.success('Copie !'); }}
                    className="mt-1 text-[9px] text-purple-400 hover:text-purple-300 flex items-center gap-0.5">
                    <Copy size={8} /> Copier
                  </button>
                </div>
              )}
              <div className="space-y-1">
                {aiTips.conseils.map((c, i) => (
                  <div key={i} className="flex items-start gap-1.5 text-[11px] text-white/60">
                    <span className="text-green-400 shrink-0 mt-0.5">{'\u25CF'}</span>
                    <span>{c}</span>
                  </div>
                ))}
              </div>
              {aiTips.erreurs.length > 0 && (
                <div className="mt-2 pt-2 border-t border-white/5">
                  <p className="text-[9px] text-red-400/70 font-bold mb-1">A EVITER</p>
                  {aiTips.erreurs.map((e, i) => (
                    <div key={i} className="flex items-start gap-1.5 text-[10px] text-red-400/50">
                      <span className="shrink-0 mt-0.5">{'\u2715'}</span>
                      <span>{e}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!action && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                {p.tel && (
                  <button onClick={() => { window.open(`tel:${p.tel}`); setAction('call'); }}
                    className="bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 text-green-400 rounded-xl py-4 flex flex-col items-center gap-2 transition-all"
                    data-testid="go-action-call">
                    <Phone size={24} /><span className="text-sm font-bold">Appeler</span>
                  </button>
                )}
                <button onClick={() => { setAction('email'); generateEmail(); }}
                  className="bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-400 rounded-xl py-4 flex flex-col items-center gap-2 transition-all"
                  data-testid="go-action-email">
                  <Sparkles size={24} /><span className="text-sm font-bold">Email IA</span>
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setAction('relance')}
                  className="bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 text-amber-400 rounded-xl py-3 flex items-center justify-center gap-2 transition-all"
                  data-testid="go-action-relance">
                  <Calendar size={18} /><span className="text-sm font-bold">Planifier relance</span>
                </button>
                <button onClick={() => nextProspect('passes')}
                  className="bg-white/5 hover:bg-white/10 border border-white/10 text-white/40 rounded-xl py-3 flex items-center justify-center gap-2 transition-all"
                  data-testid="go-action-skip">
                  <ChevronRight size={18} /><span className="text-sm">Passer</span>
                </button>
              </div>
            </div>
          )}

          {action === 'call' && (
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-green-500/20 space-y-3">
              <h3 className="text-sm font-bold text-green-400 flex items-center gap-2"><Phone size={14} /> Resultat de l'appel</h3>
              <div className="grid grid-cols-2 gap-1.5">
                {RESULTATS_APPEL.map(r => (
                  <button key={r.value} onClick={() => setCallResult(r.value)}
                    className={`text-xs py-2 px-2 rounded-lg border transition-all ${callResult === r.value ? 'bg-white/20 border-white/40 text-white font-bold' : 'border-white/10 text-white/50 hover:border-white/20'}`}
                    data-testid={`call-result-${r.value}`}>
                    {r.label}
                  </button>
                ))}
              </div>
              <textarea value={callNote} onChange={e => setCallNote(e.target.value)}
                placeholder="Notes sur l'appel..." rows={2}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 resize-none" />
              <div className="flex gap-2">
                <Button onClick={logCall} disabled={!callResult} className="flex-1 bg-green-500 hover:bg-green-600 text-white" data-testid="log-call-btn">Enregistrer</Button>
                <Button onClick={() => setAction(null)} variant="outline" className="border-white/20 text-white/50 hover:bg-white/5">Retour</Button>
              </div>
            </div>
          )}

          {action === 'email' && (
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-blue-500/20 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-blue-400 flex items-center gap-2"><Sparkles size={14} /> Email IA</h3>
                {!emailLoading && emailVersions.length > 0 && (
                  <Button onClick={generateEmail} variant="outline" size="sm" className="border-white/20 text-white/50 text-xs h-6 hover:bg-white/5"><RefreshCw size={10} className="mr-1" /> Regenerer</Button>
                )}
              </div>
              {emailLoading ? (
                <div className="flex flex-col items-center py-8 gap-3">
                  <Loader2 size={28} className="animate-spin text-blue-400" />
                  <p className="text-sm text-white/50">L'IA redige 3 versions...</p>
                  {p.siteWeb && <p className="text-xs text-white/30">Analyse du site en cours...</p>}
                </div>
              ) : (
                <>
                  {emailVersions.length > 1 && (
                    <div className="grid grid-cols-3 gap-1.5">
                      {emailVersions.map((v, i) => {
                        const cfg = { professionnel: { l: 'Formel', c: 'blue' }, decontracte: { l: 'Cool', c: 'green' }, direct: { l: 'Direct', c: 'orange' } };
                        const c = cfg[v.ton] || { l: v.ton, c: 'gray' };
                        return (
                          <button key={i} onClick={() => { setSelectedVersion(i); setEmailForm(prev => ({ ...prev, subject: v.subject, body: v.body })); }}
                            className={`text-xs py-2 rounded-lg border transition-all ${selectedVersion === i ? 'bg-white/15 border-white/30 text-white font-bold' : 'border-white/10 text-white/40 hover:border-white/20'}`}
                            data-testid={`go-email-version-${v.ton}`}>
                            {c.l}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <input value={emailForm.subject} onChange={e => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30"
                    placeholder="Objet..." data-testid="go-email-subject" />
                  <textarea value={emailForm.body} onChange={e => setEmailForm(prev => ({ ...prev, body: e.target.value }))}
                    rows={8} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 resize-none leading-relaxed"
                    data-testid="go-email-body" />
                  <div className="flex gap-2">
                    <Button onClick={sendEmail} disabled={sendingEmail || !p.email} className="flex-1 bg-blue-500 hover:bg-blue-600 text-white" data-testid="go-send-email-btn">
                      {sendingEmail ? <><Loader2 size={14} className="animate-spin mr-1" /> Envoi...</> : <><Send size={14} className="mr-1" /> Envoyer</>}
                    </Button>
                    <Button onClick={async () => {
                      try {
                        const res = await fetch(`${API}/api/crm/email-schedule`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ prospect_id: p.id, subject: emailForm.subject, body: emailForm.body, sender: emailForm.sender || 'restaurant',
                            send_at: new Date(Date.now() + 24*60*60*1000).toISOString() }) });
                        if (res.ok) { toast.success('Email programme !'); nextProspect('emails'); }
                      } catch { toast.error('Erreur'); }
                    }} variant="outline" className="border-amber-400/30 text-amber-400 hover:bg-amber-500/10" data-testid="go-schedule-email">
                      <Clock size={14} />
                    </Button>
                    <Button onClick={() => { const txt = `${emailForm.subject}\n\n${emailForm.body}`; navigator.clipboard.writeText(txt); toast.success('Copie !'); nextProspect('emails'); }}
                      variant="outline" className="border-white/20 text-white/50 hover:bg-white/5"><Copy size={14} /></Button>
                    <Button onClick={() => setAction(null)} variant="outline" className="border-white/20 text-white/50 hover:bg-white/5">Retour</Button>
                  </div>
                  <Button onClick={async () => {
                    try {
                      const res = await fetch(`${API}/api/crm/email-sequence`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ prospect_id: p.id, sender: emailForm.sender || 'restaurant' }) });
                      const data = await res.json();
                      if (res.ok) { toast.success(`Sequence ${data.count} emails programmee !`); nextProspect('emails'); }
                    } catch { toast.error('Erreur'); }
                  }} variant="outline" className="w-full border-purple-400/30 text-purple-400 hover:bg-purple-500/10 text-xs" data-testid="go-create-sequence">
                    <Zap size={12} className="mr-1" /> Sequence auto (3 emails IA)
                  </Button>
                </>
              )}
            </div>
          )}

          {action === 'relance' && (
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-amber-500/20 space-y-3">
              <h3 className="text-sm font-bold text-amber-400 flex items-center gap-2"><Calendar size={14} /> Planifier une relance</h3>
              <div className="grid grid-cols-2 gap-1.5">
                {PROCHAINES_ACTIONS.filter(a => a.value).map(a => {
                  const days = parseInt(a.value.replace('j+', ''));
                  const d = new Date(); d.setDate(d.getDate() + days);
                  const dateStr = d.toISOString().split('T')[0];
                  return (
                    <button key={a.value} onClick={() => setRelanceDate(dateStr)}
                      className={`text-xs py-2.5 px-2 rounded-lg border transition-all ${relanceDate === dateStr ? 'bg-amber-500/20 border-amber-400/40 text-amber-400 font-bold' : 'border-white/10 text-white/50 hover:border-white/20'}`}
                      data-testid={`relance-${a.value}`}>
                      {a.label}
                    </button>
                  );
                })}
              </div>
              <input type="date" value={relanceDate} onChange={e => setRelanceDate(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
              <div className="flex gap-2">
                <Button onClick={scheduleRelance} disabled={!relanceDate} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white" data-testid="schedule-relance-btn">Planifier</Button>
                <Button onClick={() => setAction(null)} variant="outline" className="border-white/20 text-white/50 hover:bg-white/5">Retour</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
